import { AcademicTitleEnum, type AcademicTitleType } from '@app/icare/db/schema/common'
import { DoctorEditDrawer } from '@app/icare/routes/_app/praxen/-components/DoctorEditDrawer'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { QueryBaseSchema } from '@pkg/api/query/QueryFilterSchema'
import { defineEntityConfig } from '@pkg/db/schema/EntityConfig'
import { mergeNonEmptyStrings } from '@pkg/shared/utils'
import { ButtonLink } from '@pkg/ui/button'
import { LinkIndicatorIcon } from '@pkg/ui/common/icons'
import { FormattedNameAndAddress } from '@pkg/ui/formattedContent/NameAndAddress'
import { Tag } from 'antd'
import { FaUserDoctor } from 'react-icons/fa6'
import { z } from 'zod'
import { DoctorSchema, DoctorSpecialityEnum } from './schema/Doctor'

const label = t`Praxis`

export const DoctorConfig = defineEntityConfig(() => ({
  schema: DoctorSchema,

  label,
  labelPlural: t`Praxen`,

  icon: FaUserDoctor,

  route: {
    list: '/praxen',
    edit: '/praxen/$id',
    new: '/praxen/new',
  },

  drawer: { modal: DoctorEditDrawer },

  indexColumns: [
    {
      key: 'doctor.name',
      title: label,
      fixed: 'start',
      render: (_, row) => (
        <FormattedNameAndAddress
          name={mergeNonEmptyStrings(
            [AcademicTitleEnum.$.getLabel(row.title as AcademicTitleType), row.name, row.nameComplement],
            ' ',
          )}
          address={row.address ?? undefined}
          link={{ to: '/praxen/$id', params: { id: row.id } }}
        />
      ),
    },
    {
      key: 'doctor.speciality',
      title: <Trans>Spezialisierung</Trans>,
      render: (_, row) => DoctorSpecialityEnum.$.getLabel(row.speciality),
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      sorter: false,
      fixed: 'end',
      render: (_, row) => (
        <ButtonLink to="/praxen/$id" params={{ id: row.id }} size="small" shape="round">
          <Trans>Anzeigen</Trans>
          <LinkIndicatorIcon />
        </ButtonLink>
      ),
    },
  ],

  relationSelectionColumns: [
    {
      key: 'doctor.name',
      title: label,
      listSlot: 'title',
      render: (_, row) => (
        <FormattedNameAndAddress
          name={mergeNonEmptyStrings([row.name, row.nameComplement], ' ')}
          address={row.address ?? undefined}
        />
      ),
    },
    {
      key: 'doctor.speciality',
      listSlot: 'actions',
      render: (_, row) => <Tag variant="outlined">{DoctorSpecialityEnum.$.getLabel(row.speciality)}</Tag>,
    },
  ],

  search: {
    filterSchema: QueryBaseSchema.extend({
      sortBy: z
        .enum([
          'doctor.name',
          'doctor.speciality',
          'doctor.address.streetAddress',
          'doctor.address.zipCode',
          'doctor.address.city',
        ])
        .nullish(),
    }),
    sortableFields: ['name', 'speciality', 'createdAt'] as const,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
  },

  format: {
    primary: val => val?.name,
    secondary: val => DoctorSpecialityEnum.$.getLabel(val.speciality),
  },
}))
